import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import {
  OutlinedInput,
  IconButton,
  InputAdornment,
  Tooltip,
  InputLabel,
  FormControl,
  Box,
  Stack,
  Autocomplete,
  TextField,
  useTheme,
  Button,
  CircularProgress,
  Tabs,
  Tab,
  MenuItem,
} from "@mui/material";
import { Search, SignalCellularNullTwoTone } from "@mui/icons-material";
import { ZoomIn as ViewIcon, Edit as EditIcon } from "@mui/icons-material";
import SendIcon from "@mui/icons-material/Send";
import CloseIcon from "@mui/icons-material/Close";
import { toast } from "react-toastify";

import Header from "component/Header";
import BulkApproveDialog from "component/BulkApproveDialog";
import { engineerReportsById, readAsyncStorageValues, getAllOldReports, bulkApproveCompletedTickets } from "features/service/service.slice";
import Service from "features/service/service";
import "../../App.css";
import "../../stylesheet/engReports.css";
import DataGridTable from "component/DataGridTable";
import TableSkeleton from "component/Loaders/TableSkeleton";
import { Links } from "utils/pageLinkNames.js";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import FilterHeaderDropdown from "component/ui/FilterHeaderDropdown";
import ColumnHeaderLabel from "component/ui/ColumnHeaderLabel";

const Reports = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const getDefaultFilterOptions = () => ({
    status: [],
    maintenanceCode: [],
    category: [],
    model: [],
    tech: [],
  });
  const getDefaultTabFilterState = () => ({
    searchValue: "",
    filterOptions: getDefaultFilterOptions(),
  });

  const [engineerId, setEngineerId] = React.useState("");
  const dispatch = useDispatch();
  const [serviceTickets, setServiceTickets] = useState([]);
  const [date, setDate] = useState(null);

  const { engineerReports, appConstants, allOldReports, isLoading, bulkApproval } = useSelector((state) => state.service);
  const { mode } = useSelector((state) => state.globalState);
  const { user } = useSelector((state) => state.auth);
  const [selectedTab, setSelectedTab] = useState(1);
  const [showBulkApproveDialog, setShowBulkApproveDialog] = useState(false);

  const [tickets90Days, setTickets90Days] = useState([]);
  const [ticketsMoreThan90Days, setTicketsMoreThan90Days] = useState([]);

  const [tabFilters, setTabFilters] = useState({
    1: getDefaultTabFilterState(),
    2: getDefaultTabFilterState(),
  });

  const resolveTicketTechName = React.useCallback(
    (ticket) => {
      const allUsers = appConstants?.allAppUsers || appConstants?.appUsers || [];
      const getUserNameById = (userId) =>
        allUsers.find((appUser) => `${appUser.userId}` === `${userId}`)?.name || "";

      const assigneeName = ticket?.assignee?.trim?.();
      if (assigneeName) return assigneeName;

      if (typeof ticket?.assignees === "string") return ticket.assignees;

      if (Array.isArray(ticket?.assignees)) {
        return ticket.assignees
          .map((assignee) => {
            if (typeof assignee === "string" || typeof assignee === "number") {
              return getUserNameById(assignee);
            }

            if (assignee && typeof assignee === "object") {
              return assignee.name || getUserNameById(assignee.id || assignee.userId);
            }

            return "";
          })
          .filter(Boolean)
          .join(", ");
      }

      if (ticket?.assigneeId) return getUserNameById(ticket.assigneeId);

      return "";
    },
    [appConstants?.allAppUsers, appConstants?.appUsers]
  );

  const engineerReportsData = async (userId) => {
    dispatch(engineerReportsById(userId));
  };

  const onChange = (event) => {
    const value = event.target.value;
    setEngineerId(value);
    engineerReportsData(value);
  };

  useEffect(() => {
    dispatch(engineerReportsById());
  }, []);

  useEffect(() => {
    dispatch(getAllOldReports());
  }, []);

  useEffect(() => {
    const today = new Date();
    const ninetyDaysAgo = new Date(today);
    ninetyDaysAgo.setDate(today.getDate() - 90);

    if (engineerReports?.length) {
      const tickets90 = engineerReports
        .filter((ticket) => {
          const completedDate = new Date(ticket.dateCompleted);
          return completedDate >= ninetyDaysAgo;
        })
        .map((ticket) => ({
          ...ticket,
          svcTktNumber: appConstants?.showServiceTicketId ? ticket.tktId : ticket.svcTktNumber || ticket.tktId,
          dateCreated: moment(ticket.dateCreated).format("DD MMM YYYY"),
          dateCompleted: moment(ticket.dateCompleted).format("DD MMM YYYY"),
          tech: resolveTicketTechName(ticket),
        }))
        .sort((a, b) => b.ticketId - a.ticketId);

      setTickets90Days(tickets90);
    }
  }, [engineerReports, appConstants?.showServiceTicketId, resolveTicketTechName]);

  useEffect(() => {
    if (allOldReports?.data?.length) {
      const ticketsMoreThan90Days = allOldReports.data
        .filter((ticket) => {
          const completedDate = new Date(ticket.dateCompleted);
          return completedDate;
        })
        .map((ticket) => ({
          ...ticket,
          svcTktNumber: appConstants?.showServiceTicketId ? ticket.tktId : ticket.svcTktNumber || ticket.tktId,
          dateCreated: moment(ticket.dateCreated).format("DD MMM YYYY"),
          dateCompleted: moment(ticket.dateCompleted).format("DD MMM YYYY"),
          tech: resolveTicketTechName(ticket),
        }))
        .sort((a, b) => b.ticketId - a.ticketId);

      setTicketsMoreThan90Days(ticketsMoreThan90Days);
    }
  }, [allOldReports, appConstants?.showServiceTicketId, resolveTicketTechName]);

  const currentTabFilters = tabFilters[selectedTab];
  const currentSearchValue = currentTabFilters.searchValue;
  const currentFilterOptions = currentTabFilters.filterOptions;

  const updateSelectedTabFilters = React.useCallback(
    (updater) => {
      setTabFilters((prev) => ({
        ...prev,
        [selectedTab]: updater(prev[selectedTab]),
      }));
    },
    [selectedTab]
  );

  const handleSearchChange = (event) => {
    const nextSearchValue = event.target.value;
    updateSelectedTabFilters((prev) => ({
      ...prev,
      searchValue: nextSearchValue,
    }));
  };

  const handleFilterChange = React.useCallback(
    (field, selected) => {
      const selectedArray = selected ? [selected] : [];

      updateSelectedTabFilters((prev) => ({
        ...prev,
        filterOptions: {
          ...prev.filterOptions,
          [field]: selectedArray,
        },
      }));
    },
    [updateSelectedTabFilters]
  );

  const gridReportsAction = (args) => {
    return (
      <Link to={`/${Links.SERVICE_TICKET}/` + args.row.tktId}>
        <IconButton sx={{ mr: 1 }}>
          <Tooltip title="View">
            <ViewIcon sx={{ color: mode === "dark" ? "#fff" : theme.palette.primary[50] }} />
          </Tooltip>
        </IconButton>
      </Link>
    );
  };
  const dateComparator = (v1, v2) => moment(v1).diff(moment(v2));

  const maintenanceReport = async () => {
    navigate(`${Links.MAINTENANCE_REPORT}`, {
      state: { date: date ? moment(date).format("YYYY-MM-DD") : moment(new Date()).format("YYYY-MM-DD") },
    });
  };

  const handleTabsChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  const clearAllFilters = () => {
    updateSelectedTabFilters(() => getDefaultTabFilterState());
  };

  const handleBulkApproveClick = () => {
    setShowBulkApproveDialog(true);
  };

  const handleCloseBulkApproveDialog = () => {
    setShowBulkApproveDialog(false);
  };

  const getCurrentTabData = () => {
    return selectedTab === 1 ? tickets90Days : ticketsMoreThan90Days;
  };

  const filterTickets = React.useCallback(
    (tickets, searchValue, filterOptions) => {
      return tickets.filter((ticket) => {
        if (searchValue.trim() !== "") {
          const searchLower = searchValue.toLowerCase();
          const searchMatch = Object.values(ticket).join(" ").toLowerCase().includes(searchLower);
          if (!searchMatch) return false;
        }

        if (filterOptions.status?.length && !filterOptions.status.includes(ticket.ticketStatus)) {
          return false;
        }

        if (filterOptions.maintenanceCode?.length && !filterOptions.maintenanceCode.includes(ticket.maintenanceCode)) {
          return false;
        }

        if (filterOptions.category?.length && !filterOptions.category.includes(ticket.ticketCategory)) {
          return false;
        }

        if (filterOptions.model?.length && !filterOptions.model.includes(ticket.machineModel)) {
          return false;
        }

        if (filterOptions.tech?.length && !filterOptions.tech.includes(ticket.tech)) {
          return false;
        }

        return true;
      });
    },
    []
  );

  const filteredTickets90Days = React.useMemo(
    () => filterTickets(tickets90Days, tabFilters[1].searchValue, tabFilters[1].filterOptions),
    [filterTickets, tabFilters, tickets90Days]
  );
  const filteredTicketsMoreThan90Days = React.useMemo(
    () => filterTickets(ticketsMoreThan90Days, tabFilters[2].searchValue, tabFilters[2].filterOptions),
    [filterTickets, tabFilters, ticketsMoreThan90Days]
  );

  const getFilteredData = () => {
    return selectedTab === 1 ? filteredTickets90Days : filteredTicketsMoreThan90Days;
  };

  const reportGrid = [
    {
      field: "svcTktNumber",
      headerName: "Ticket ID",
      minWidth: 100,
      flex: 0.10,
      headerAlign: "left",
      align: "left",
      renderHeader: () => <ColumnHeaderLabel label="Ticket ID" />,
      valueGetter: (params) => {
        return appConstants?.showServiceTicketId ? params.row.tktId : params.row.svcTktNumber;
      },
      renderCell: (params) => {
        const displayValue = appConstants?.showServiceTicketId ? params.row.tktId : params.value;
        return (
          <span
            style={{
              cursor: "pointer",
              textDecoration: "underline",
            }}
            onClick={() => navigate(`/${Links.SERVICE_TICKET}/` + params.row.tktId)}
          >
            {displayValue}
          </span>
        );
      },
    },
    {
      field: "dateCreated",
      headerName: "Created",
      minWidth: 120,
      flex: 0.2,
      headerAlign: "left",
      align: "left",
      sortComparator: dateComparator,
      renderHeader: () => <ColumnHeaderLabel label="Created" />,
    },
    {
      field: "dateCompleted",
      headerName: "Completed",
      minWidth: 130,
      flex: 0.3,
      headerAlign: "left",
      align: "left",
      sortComparator: dateComparator,
      renderHeader: () => <ColumnHeaderLabel label="Completed" />,
    },
    {
      field: "tech",
      headerName: "Tech",
      minWidth: 150,
      flex: 0.4,
      headerAlign: "left",
      align: "left",
      valueGetter: (params) => params.row.tech || resolveTicketTechName(params.row),
      renderHeader: () => {
        const currentData = getCurrentTabData();
        const techOptions = [...new Set(currentData.map((t) => t.tech).filter(Boolean))];

        return (
          <FilterHeaderDropdown
            label="Tech"
            value={currentFilterOptions.tech?.[0] || ""}
            options={techOptions}
            onChange={(selected) => handleFilterChange("tech", selected)}
          />
        );
      },
    },
    {
      field: "maintenanceCode",
      headerName: "Maintenance code",
      minWidth: 150,
      flex: 0.3,
      headerAlign: "left",
      align: "left",
      renderHeader: () => {
        const currentData = getCurrentTabData();
        const maintenanceCodes = [...new Set(currentData.map((t) => t.maintenanceCode).filter(Boolean))];

        return (
          <FilterHeaderDropdown
            label="Maint. Code"
            value={currentFilterOptions.maintenanceCode?.[0] || ""}
            options={maintenanceCodes}
            onChange={(selected) => handleFilterChange("maintenanceCode", selected)}
          />
        );
      },
    },
    {
      field: "serialNo",
      headerName: "Serial Number",
      minWidth: 150,
      flex: 0.3,
      headerAlign: "left",
      align: "left",
      renderHeader: () => <ColumnHeaderLabel label="Serial Number" />,
    },
    {
      field: "machineModel",
      headerName: "Model",
      minWidth: 150,
      flex: 0.2,
      headerAlign: "left",
      align: "left",
      renderHeader: () => {
        const currentData = getCurrentTabData();
        const models = [...new Set(currentData.map((t) => t.machineModel).filter(Boolean))];

        return (
          <FilterHeaderDropdown
            label="Model"
            value={currentFilterOptions.model?.[0] || ""}
            options={models}
            onChange={(selected) => handleFilterChange("model", selected)}
          />
        );
      },
    },
    {
      field: "ticketCategory",
      headerName: "Category",
      minWidth: 170,
      flex: 0.3,
      headerAlign: "left",
      align: "left",
      renderHeader: () => {
        const currentData = getCurrentTabData();
        const categories = [...new Set(currentData.map((t) => t.ticketCategory).filter(Boolean))];

        return (
          <FilterHeaderDropdown
            label="Category"
            value={currentFilterOptions.category?.[0] || ""}
            options={categories}
            onChange={(selected) => handleFilterChange("category", selected)}
          />
        );
      },
    },
    {
      field: "ticketStatus",
      headerName: "Status",
      minWidth: 90,
      flex: 1.1,
      headerAlign: "left",
      align: "left",
      // flex: 0.2,
      // {
      //   headerName: "",
      //   minWidth: 60,
      //   flex: 0.35,
      //   sortable: false,
      //   filterable: false,
      //   renderCell: gridReportsAction,
      // },
      renderHeader: () => {
        const currentData = getCurrentTabData();
        const statuses = [...new Set(currentData.map((t) => t.ticketStatus).filter(Boolean))];

        return (
          <FilterHeaderDropdown
            label="Status"
            value={currentFilterOptions.status?.[0] || ""}
            options={statuses}
            onChange={(selected) => handleFilterChange("status", selected)}
          />
        );
      },
    },
  ];

  return (
    <div>
      <Stack sx={{ ml: 2, mt: 2, mb: 1 }}>
        <Header titles={["Reports"]} />
      </Stack>
      <Box sx={{ mx: 2 }}>
        <Stack direction={"row"} gap={1} alignItems={"center"} my={2}>
          <OutlinedInput
            id="outlined-adornment-password"
            value={currentSearchValue}
            onChange={(event) => handleSearchChange(event)}
            placeholder="Search..."
            endAdornment={
              <InputAdornment position="end">
                <IconButton aria-label="toggle password visibility" edge="end">
                  <Search />
                </IconButton>
              </InputAdornment>
            }
            sx={{ minWidth: 300, maxWidth: 600, height: 35 }}
          />
          {/* <Autocomplete
            onChange={onChange}
            size="small"
            value={engineerId ? appConstants?.appUsers.find((user) => user.userId === engineerId) : null}
            options={
              Object.keys(appConstants).length
                ? appConstants?.appUsers.filter((obj) => {
                  return obj.userRole === appConstants.allTypes.USER_ROLES.ROLE_SERVICE_TECHNICIAN;
                })
                : []
            }
            getOptionLabel={(option) => option.nameEmpno}
            renderInput={(params) => <TextField {...params} label="Select technician" />}
            sx={{ width: 300 }}
            renderOption={(props, option) => (
              <MenuItem {...props} key={option.userId} value={option.userId}>
                {option.nameEmpno}
              </MenuItem>
            )}
          /> */}

          <LocalizationProvider dateAdapter={AdapterMoment}>
            <DatePicker
              maxDate={moment()}
              label="Select date"
              format={"DD MMM YYYY"}
              value={date ? moment(date) : null}
              onChange={(newValue) => setDate(newValue)}
              slotProps={{ textField: { size: "small" } }}
            />
          </LocalizationProvider>
          <Button
            // startIcon={emailSending ? <CircularProgress size={12} /> : <SendIcon />}
            sx={{ height: "self" }}
            variant="contained"
            autoCapitalize="none"
            onClick={() => maintenanceReport()}
          >
            Maintenance Report
          </Button>
          <Button
            startIcon={<CloseIcon />}
            sx={{ height: "self" }}
            autoCapitalize="none"
            variant="contained"
            onClick={clearAllFilters}
          >
            Clear All
          </Button>
          {user?.roles?.includes(appConstants?.allTypes?.USER_ROLES?.ROLE_MAINTENANCE_SUPERVISOR) && (
            <Button
              variant="contained"
              onClick={handleBulkApproveClick}
              disabled={bulkApproval?.isLoading}
              sx={{ height: "self" }}
              autoCapitalize="none"
            >
              {bulkApproval?.isLoading ? "Processing..." : "Bulk Approve"}
            </Button>
          )}
        </Stack>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs value={selectedTab} onChange={handleTabsChange} aria-label="service tickets tabs" centered>
            <Tab
              sx={{
                fontWeight: "bold",
                fontSize: "0.9rem",
                marginRight: "150px",
                "&:hover": {
                  backgroundColor: "initial",
                  color: "initial",
                },
              }}
              label={`60 Days (${filteredTickets90Days.length})`}
              value={1}
            />
            <Tab
              sx={{
                fontWeight: "bold",
                fontSize: "0.9rem",
                marginRight: "120px",
                "&:hover": {
                  backgroundColor: "initial",
                  color: "initial",
                },
              }}
              label={`More Than 60 Days (${filteredTicketsMoreThan90Days.length})`}
              value={2}
            />
          </Tabs>
        </Box>
        {isLoading ? (
          <TableSkeleton rows={8} columns={reportGrid.length} />
        ) : (
          <DataGridTable
            rowId={"tktId"}
            data={getFilteredData()}
            columnAttributes={reportGrid}
            density="compact"
            sx={{
              "& .MuiDataGrid-cell": { px: 1, py: 0.5 },
              "& .MuiDataGrid-columnHeader": { px: 1 },
              "& .MuiDataGrid-columnHeaderTitle": { fontSize: "0.8rem" },
              "& .MuiDataGrid-cellContent": { fontSize: "0.8rem" },
            }}
          />
        )}
      </Box>

      <BulkApproveDialog
        open={showBulkApproveDialog}
        onClose={handleCloseBulkApproveDialog}
        loading={bulkApproval?.isLoading}
        onConfirm={(days) => {
          dispatch(bulkApproveCompletedTickets(days)).then((res) => {
            if (!res.error) {
              toast.success(res?.payload?.message || res.payload || "Bulk approval started. You can continue working.");
              handleCloseBulkApproveDialog();
            } else {
              toast.error(res?.payload?.message || res.payload || "Bulk approval failed.");
            }
          });
        }}
      />
    </div>
  );
};
export default Reports;